import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import CardRegistrationModal from "../../components/ui/CardRegistrationModal";
import NFCCodeModal from "../../components/ui/NFCCodeModal";
import FilterBar from "./components/FilterBar";
import BulkActionsBar from "./components/BulkActionsBar";
import CardTable from "./components/CardTable";
import Pagination from "./components/Pagination";
import StatsCards from "./components/StatsCards";
import { useGetallFunctionQuery } from "store/dynamicApi";

const CardManagement = () => {
  // Mock data for cards
  const mockCards = [
    {
      id: "card-001",
      cardId: "NFC-001",
      cardName: "John Doe Business Card",
      assignedUser: "John Doe",
      userEmail: "john.doe@company.com",
      userType: "individual",
      department: "Sales",
      status: "active",
      registrationDate: "2024-01-15T10:30:00Z",
      lastPayment: {
        amount: 299.99,
        date: "2024-01-20T14:45:00Z",
      },
      maxShares: 100,
      expiryDate: "2024-12-31T23:59:59Z",
      nfcCode: "NFC001ABC123",
    },
    {
      id: "card-002",
      cardId: "NFC-002",
      cardName: "Marketing Team Card",
      assignedUser: "Sarah Johnson",
      userEmail: "sarah.johnson@company.com",
      userType: "team",
      department: "Marketing",
      status: "active",
      registrationDate: "2024-01-10T09:15:00Z",
      lastPayment: {
        amount: 599.99,
        date: "2024-01-25T11:20:00Z",
      },
      maxShares: 500,
      expiryDate: "2024-12-31T23:59:59Z",
      nfcCode: "NFC002DEF456",
    },
    {
      id: "card-003",
      cardId: "NFC-003",
      cardName: "Michael Chen Personal Card",
      assignedUser: "Michael Chen",
      userEmail: "michael.chen@company.com",
      userType: "individual",
      department: "Engineering",
      status: "restricted",
      registrationDate: "2024-01-08T16:20:00Z",
      lastPayment: {
        amount: 199.99,
        date: "2024-01-12T13:30:00Z",
      },
      maxShares: 50,
      expiryDate: "2024-06-30T23:59:59Z",
      nfcCode: "NFC003GHI789",
    },
    {
      id: "card-004",
      cardId: "NFC-004",
      cardName: "Emily Rodriguez Event Card",
      assignedUser: "Emily Rodriguez",
      userEmail: "emily.rodriguez@company.com",
      userType: "individual",
      department: "Events",
      status: "inactive",
      registrationDate: "2024-01-05T12:45:00Z",
      lastPayment: null,
      maxShares: 25,
      expiryDate: "2024-03-31T23:59:59Z",
      nfcCode: "NFC004JKL012",
    },
    {
      id: "card-005",
      cardId: "NFC-005",
      cardName: "Development Team Card",
      assignedUser: "Alex Thompson",
      userEmail: "alex.thompson@company.com",
      userType: "team",
      department: "Engineering",
      status: "active",
      registrationDate: "2024-01-12T08:30:00Z",
      lastPayment: {
        amount: 899.99,
        date: "2024-01-28T10:15:00Z",
      },
      maxShares: 1000,
      expiryDate: "2024-12-31T23:59:59Z",
      nfcCode: "NFC005MNO345",
    },
    {
      id: "card-006",
      cardId: "NFC-006",
      cardName: "Lisa Wang Executive Card",
      assignedUser: "Lisa Wang",
      userEmail: "lisa.wang@company.com",
      userType: "individual",
      department: "Executive",
      status: "active",
      registrationDate: "2024-01-18T14:00:00Z",
      lastPayment: {
        amount: 499.99,
        date: "2024-01-30T16:45:00Z",
      },
      maxShares: 200,
      expiryDate: "2024-12-31T23:59:59Z",
      nfcCode: "NFC006PQR678",
    },
  ];

  // State management
  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [cardId, setCardId] = useState({});
  const [filters, setFilters] = useState({
    status: "all",
    userType: "all",
    dateRange: "all",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "registrationDate",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showNFCModal, setShowNFCModal] = useState(false);
  const [selectedCardForNFC, setSelectedCardForNFC] = useState(null);

  const { data, isLoading } = useGetallFunctionQuery({ url: "/card/all" });



  useEffect(() => {
    //setCards(data)
    if (data) {
      setCards(data);
    }
  }, [data]);

  // Filter and search logic
  const filteredCards = useMemo(() => {
    let filtered = [...cards];

    // Apply status filter
    if (filters.status !== "all") {
      filtered = filtered.filter((card) => card.status === filters.status);
    }

    // Apply user type filter
    if (filters.userType !== "all") {
      filtered = filtered.filter((card) => card.userType === filters.userType);
    }

    // Apply date range filter
    if (filters.dateRange !== "all") {
      const now = new Date();
      const filterDate = new Date();

      switch (filters.dateRange) {
        case "today":
          filterDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          filterDate.setDate(now.getDate() - 7);
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case "quarter":
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }

      if (filters.dateRange !== "all") {
        filtered = filtered.filter(
          (card) => new Date(card.registrationDate) >= filterDate
        );
      }
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered.filter(
        (card) =>
          card.hcId?.toLowerCase().includes(query) ||
          card.cardCode?.toLowerCase().includes(query) ||
          card.content?.name?.toLowerCase().includes(query) ||
          card.content?.title?.toLowerCase().includes(query)
          // card.department?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [cards, filters, searchQuery]);

  // Sort logic
  const sortedCards = useMemo(() => {
    if (!sortConfig.key) return filteredCards;

    return [...filteredCards].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key === "lastPayment") {
        aValue = a.lastPayment?.amount || 0;
        bValue = b.lastPayment?.amount || 0;
      }

      // Handle date strings
      if (sortConfig.key.includes("Date")) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle strings
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredCards, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);
  const paginatedCards = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedCards.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedCards, currentPage, itemsPerPage]);

  // Statistics calculation
  const stats = useMemo(() => {
    return {
      totalCards: cards.length,
      activeCards: cards.filter((card) => card.status === 0).length,
      restrictedCards: cards.filter((card) => card.status === 1)
        .length,
      inactiveCards: cards.filter((card) => card.status === 2).length,
    };
  }, [cards]);

  // Event handlers
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1);
  };

  const handleSearchChange = (query) => {
    console.log("Search Query:", query);
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      key: column,
      direction:
        prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectCard = (cardId) => {
    setSelectedCards((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCards.length === paginatedCards.length) {
      setSelectedCards([]);
    } else {
      setSelectedCards(paginatedCards.map((card) => card.id));
    }
  };

  const handleBulkAction = (action, cardIds) => {
    switch (action) {
      case "activate":
        setCards((prev) =>
          prev.map((card) =>
            cardIds.includes(card.id) ? { ...card, status: "active" } : card
          )
        );
        break;
      case "restrict":
        setCards((prev) =>
          prev.map((card) =>
            cardIds.includes(card.id) ? { ...card, status: "restricted" } : card
          )
        );
        break;
      case "deactivate":
        setCards((prev) =>
          prev.map((card) =>
            cardIds.includes(card.id) ? { ...card, status: "inactive" } : card
          )
        );
        break;
      case "export":
        // Simulate export functionality
        const selectedCardsData = cards.filter((card) =>
          cardIds.includes(card.id)
        );
        console.log("Exporting cards:", selectedCardsData);
        break;
      default:
        break;
    }
    setSelectedCards([]);
  };

  const handleViewNFCCode = (card) => {
    console.log("Viewing NFC Code for card:", card);
    setSelectedCardForNFC(card);
    setShowNFCModal(true);
  };

  const handleEditAssignment = (card) => {
    // Simulate edit assignment functionality
    console.log("Editing assignment for card:", card);
  };

  const handleToggleStatus = (card) => {
    const newStatus = card.status === "active" ? "restricted" : "active";
    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, status: newStatus } : c))
    );
  };

  const handleRegisterCard = (cardData) => {
    const newCard = {
      ...cardData,
      registrationDate: new Date().toISOString(),
      lastPayment: null,
    };
    setCards((prev) => [newCard, ...prev]);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery]);



  return (
    <>
      <Helmet>
        <title>Card Management - NFC Card Admin</title>
        <meta
          name="description"
          content="Comprehensive NFC digital card management with advanced filtering, search, and bulk operations."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />

        <main className="lg:ml-60 pt-16">
          <div className="p-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                Card Management
              </h1>
              <p className="text-text-muted">
                Manage NFC digital cards, assignments, and access controls
              </p>
            </div>

            {/* Statistics Cards */}
            <StatsCards stats={stats} />

            {/* Filter Bar */}
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onRegisterCard={() => setShowRegistrationModal(true)}
            />

            {/* Bulk Actions Bar */}
            <BulkActionsBar
              selectedCards={selectedCards}
              onBulkAction={handleBulkAction}
              onClearSelection={() => setSelectedCards([])}
              totalCards={paginatedCards?.length}
            />

            {/* Card Table */}
            <CardTable
              cards={paginatedCards}
              selectedCards={selectedCards}
              onSelectCard={handleSelectCard}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              sortConfig={sortConfig}
              onViewNFCCode={handleViewNFCCode}
              onEditAssignment={handleEditAssignment}
              onToggleStatus={handleToggleStatus}
              setCardId={setCardId}
            />

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={sortedCards.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </main>

        {/* Modals */}
        <CardRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => setShowRegistrationModal(false)}
          onSubmit={handleRegisterCard}
        />

        <NFCCodeModal
          isOpen={showNFCModal}
          onClose={() => {
            setShowNFCModal(false);
            setSelectedCardForNFC(null);
          }}
          cardData={selectedCardForNFC}
        />
      </div>
    </>
  );
};

export default CardManagement;
